import type { OrderItemLineOption } from "@/components/orders/ItemOptionsList"
import {
  formatReducedFraction,
  getPortionFractionBase,
  getPortionTarget,
  resolveEffectivePortionQuantities,
} from "@/lib/option-group-portions"
import type { CartItem, ItemOptionsQuantities, OptionGroup } from "@/types"

function groupOptionLines(cartItem: CartItem, optionGroup: OptionGroup): OrderItemLineOption[] {
  const quantities: ItemOptionsQuantities = cartItem.optionQuantities[optionGroup.id] ?? {}
  const portionTarget = getPortionTarget(optionGroup)
  const effectiveQuantities = portionTarget ? resolveEffectivePortionQuantities(quantities, portionTarget) : quantities
  const portionFractionBase = portionTarget ? getPortionFractionBase(quantities, portionTarget) : null
  const hasDivided =
    !portionTarget &&
    optionGroup.minPermitted > 1 &&
    (optionGroup.priceMethod === "MAX" || optionGroup.priceMethod === "AVG")

  return optionGroup.options.flatMap((option) => {
    const rawQuantity = quantities[option.id] ?? 0
    if (rawQuantity <= 0) return []

    const baseName = cartItem.itemOptions[option.id]?.name ?? ""
    const isPortionEntry = Boolean(portionTarget && (effectiveQuantities[option.id] ?? 0) > 0)
    const portionPrefix =
      portionFractionBase && rawQuantity < portionFractionBase
        ? `${formatReducedFraction(rawQuantity, portionFractionBase)} `
        : ""
    const name = portionPrefix
      ? `${portionPrefix}${baseName}`
      : hasDivided
        ? `1/${optionGroup.minPermitted} ${baseName}`
        : baseName

    return [
      {
        key: `${optionGroup.id}-${option.id}`,
        name,
        quantityLabel: String(isPortionEntry ? 1 : rawQuantity),
        note: cartItem.optionSpecialInstructions?.[optionGroup.id]?.[option.id] || undefined,
      },
    ]
  })
}

/** Selected options of a cart item as item-line options: portion fractions folded into the name, notes attached. */
export function cartItemOptionLines(cartItem: CartItem): OrderItemLineOption[] {
  return cartItem.optionGroups.flatMap((optionGroup) => groupOptionLines(cartItem, optionGroup))
}
